import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import mapLodash from 'lodash/map';
import { computeAutoSelectedSellers, getSelectList } from '../../config/utils';
import { fetchCompanyFilters, getFilterSellers } from '../../store/actions/rateCard';
import { getAllSellersList } from '../../store/actions';

// Onboarding status enum mirrors Tbl_OnboardingStatusMaster.
// If the backend seeded these ids differently, adjust here or swap for a
// lookup endpoint — the FE brief permits either.
export const ONBOARDING_STATUS_OPTIONS = [
    { value: 1, label: 'Draft' },
    { value: 3, label: 'Submitted' },
    { value: 4, label: 'Under Review' },
    { value: 5, label: 'Rejected' },
    { value: 6, label: 'Approved' },
    { value: 7, label: 'Active' },
    { value: 9, label: 'Email Verification Pending' },
    { value: 10, label: 'Email Verified' },
];

const UserRequestFilterPanel = ({
    showFilter,
    initialCompanies,
    initialSellers,
    initialStatuses,
    onApply,
    onClear,
}) => {
    // Primary dropdown data mirrors the Inventory filter: companies come from
    // /api/company-filter, sellers from /api/seller-filter, and dependent
    // sellers on company change come from /api/GetSellersByCompanyIds.
    const dispatch = useDispatch();
    const { filterCompanies, filterAllSellers } = useSelector(state => state.RateCardReducer);

    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const [allSellers, setAllSellers] = useState([]);
    const [filterData, setFilterData] = useState({
        companyIdList: initialCompanies || [],
        sellerIdList: initialSellers || [],
        statusIdList: initialStatuses || [],
    });

    useEffect(() => {
        dispatch(fetchCompanyFilters({ pageIndex: 1, pageSize: 10, sortColumn: 'name', sortOrder: 'ascending' }));
        dispatch(getFilterSellers({
            pageIndex: 1,
            pageSize: 10,
            sortColumn: 'name',
            sortOrder: 'ASCENDING',
            companyIdList: [0],
            screenType: 2,
        }));
    }, [dispatch]);

    useEffect(() => {
        const companies = filterCompanies?.Entity?.listCompanyMasterOrdersEntity || [];
        const sellers = filterAllSellers?.EntityList || [];
        if (companies.length) {
            setCompanyList(getSelectList(companies, 'Id', 'name'));
        }
        if (sellers.length) {
            const opts = getSelectList(sellers, 'id', 'sellerAndCompany');
            setAllSellers(opts);
            setSellerList(opts);
        }
    }, [filterCompanies, filterAllSellers]);

    const fetchSellersForCompanies = async (companyIds) => {
        if (!companyIds.length) {
            setSellerList(allSellers);
            return;
        }
        try {
            const res = await getAllSellersList({ companyIds });
            if (res?.data?.Status === 200) {
                const list = (res.data.Entity?.listSellerMasterOrdersEntity || []).map(item => ({
                    ...item,
                    label: item.sellerAndCompany,
                    value: item.id,
                }));
                setSellerList(list);
                setFilterData(prev => {
                    const merged = computeAutoSelectedSellers(list, companyIds, prev.sellerIdList || []);
                    if (merged.length === (prev.sellerIdList?.length || 0)) return prev;
                    return { ...prev, sellerIdList: merged };
                });
            }
        } catch {
            // Silent — dropdown just won't refresh.
        }
    };

    const handleMultiSelect = (value, fieldName) => {
        const nextValue = value || [];
        if (fieldName === 'companyIdList') {
            const companyIds = mapLodash(nextValue, 'Id');
            fetchSellersForCompanies(companyIds);
            // Drop any previously-selected seller whose company is no longer picked.
            const remainingSellers = (filterData.sellerIdList || []).filter(s =>
                companyIds.includes(Number.parseInt(s.companyIds, 10)) // NOSONAR
            );
            setFilterData({ ...filterData, companyIdList: nextValue, sellerIdList: remainingSellers });
        } else {
            setFilterData({ ...filterData, [fieldName]: nextValue });
        }
    };

    const handleApply = () => {
        onApply({
            companies: filterData.companyIdList || [],
            sellers: filterData.sellerIdList || [],
            statuses: filterData.statusIdList || [],
        });
    };

    const handleReset = () => {
        setFilterData({ companyIdList: [], sellerIdList: [], statusIdList: [] });
        setSellerList(allSellers);
        onClear();
    };

    return (
        <div className={showFilter
            ? 'filterWrapperGradient filterContainer filterContainerZIndex show historyFilter zIndex2 pt-2'
            : 'filterContainer historyFilter'}>
            <div className="decorativeElem"></div>
            <div className="row mb10">
                <div className="col-sm-12 col-lg-3 mobMb-10">
                    <div className="row">
                        <div className="col-12">
                            <div className="labelHead">Referred By (Company)</div>
                        </div>
                        <div className="col-12 fontFix">
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                options={companyList}
                                classNamePrefix="list"
                                id="companyIdList"
                                name="companyIdList"
                                value={filterData.companyIdList}
                                onChange={(value) => handleMultiSelect(value, 'companyIdList')}
                                placeholder="Select Company..."
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-sm-12 col-lg-3 mobMb-10">
                    <div className="row">
                        <div className="col-12">
                            <div className="labelHead">Referred By (Seller)</div>
                        </div>
                        <div className="col-12 fontFix">
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                options={sellerList}
                                classNamePrefix="list"
                                id="sellerIdList"
                                name="sellerIdList"
                                value={filterData.sellerIdList}
                                onChange={(value) => handleMultiSelect(value, 'sellerIdList')}
                                placeholder="Select Seller..."
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-sm-12 col-lg-3 mobMb-10">
                    <div className="row">
                        <div className="col-12">
                            <div className="labelHead">Status</div>
                        </div>
                        <div className="col-12 fontFix">
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                options={ONBOARDING_STATUS_OPTIONS}
                                classNamePrefix="list"
                                id="statusIdList"
                                name="statusIdList"
                                value={filterData.statusIdList}
                                onChange={(value) => handleMultiSelect(value, 'statusIdList')}
                                placeholder="Select Status..."
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-lg-3 col-sm-12">
                    <div className="row">
                        <div className="col-12">
                            <div className="hidLbl">-</div>
                        </div>
                        <div className="col-12 flexEnd">
                            <button
                                type="button"
                                className="btn cus-primary-transparent-btn top-btn"
                                onClick={handleReset}
                            >
                                Clear Filter
                            </button>
                            <button
                                type="button"
                                className="cus-seconday-bg-btn btn filterBtn"
                                onClick={handleApply}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

UserRequestFilterPanel.propTypes = {
    showFilter: PropTypes.bool,
    initialCompanies: PropTypes.arrayOf(PropTypes.object),
    initialSellers: PropTypes.arrayOf(PropTypes.object),
    initialStatuses: PropTypes.arrayOf(PropTypes.object),
    onApply: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
};

export default UserRequestFilterPanel;
